// Renders a rows[][] grid (from readExcelRows) as a scrollable HTML table
// with 0-indexed row/col headers -- matching field_mappings' {row,col}
// semantics exactly (NOT spreadsheet A1 notation), so a cell clicked here can
// be stored directly as a template mapping. Used by the Template Setup
// Wizard for click-to-pick-cell field mapping.
interface Props {
  rows: unknown[][]
  maxRows?: number
  maxCols?: number
  onCellClick?: (row: number, col: number) => void
  // Highlights: map of "row,col" -> a CSS color, so already-confirmed field
  // mappings and the currently-active pick target can be shown at a glance.
  highlights?: Record<string, string>
}

export default function SpreadsheetPreview({ rows, maxRows = 40, maxCols = 15, onCellClick, highlights }: Props) {
  const rowCount = Math.min(rows.length, maxRows)
  const colCount = Math.min(Math.max(...rows.slice(0, rowCount).map(r => r?.length || 0), 1), maxCols)

  return (
    <div className="overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg max-h-[32rem]">
      <table className="text-xs border-collapse">
        <thead>
          <tr>
            <th className="sticky top-0 left-0 z-20 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1 w-10" />
            {Array.from({ length: colCount }, (_, c) => (
              <th key={c} className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1 font-mono font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap">
                Col {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }, (_, r) => (
            <tr key={r}>
              <td className="sticky left-0 z-10 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1 font-mono text-gray-500 dark:text-gray-400 text-right whitespace-nowrap">
                Row {r}
              </td>
              {Array.from({ length: colCount }, (_, c) => {
                const value = rows[r]?.[c]
                const highlight = highlights?.[`${r},${c}`]
                return (
                  <td
                    key={c}
                    onClick={onCellClick ? () => onCellClick(r, c) : undefined}
                    title={value == null ? '' : String(value)}
                    className={`border border-gray-200 dark:border-gray-700 px-2 py-1 max-w-[10rem] truncate text-gray-800 dark:text-gray-200 ${
                      onCellClick ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30' : ''
                    }`}
                    style={highlight ? { backgroundColor: highlight } : undefined}
                  >
                    {value == null || value === '' ? '' : String(value)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
